import React, { useEffect, useRef, useState } from 'react';
import JoditEditor from 'jodit-react';
import 'jodit/build/jodit.min.css';
import { CircularProgress, Tooltip } from '@material-ui/core';
import { observer } from 'mobx-react-lite';
import { inject } from 'mobx-react';
import { withStyles } from '@material-ui/core';
import classNames from 'classnames';
import MicrofoneMagicoIcon from '../../components/Icon/MicrofoneMagicoIcon';
import { Mic, Description, Send } from '@material-ui/icons';
import { Button } from '../../components/ui/Buttons';
import { aiEstruturarProntuarioConsulta, aiEstruturarProntuarioRetorno, uploadArquivoAudio, enviarArquivosAudioParaResumo } from '../../services/AiService';
import blobToBase64 from '../../utils/blobToBase64';



const AudioTextoIA = observer(({ classes, recordingState, onRecordingChange, prontuarioStore, idAgendamento, onClose, sujeitoAtencaoId, isMaximized,openNotification }) => {

  const [isRecording, setIsRecording] = useState(recordingState || false);
  const [textoBase, setTextoBase] = useState('');
  const [availableMicrophones, setAvailableMicrophones] = useState([]);
  const [selectedMicrophone, setSelectedMicrophone] = useState('');
  const [prontuarioEstruturado, setProntuarioEstruturado] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isIncluding, setIsIncluding] = useState(false);
  const [isEditingTextoBase, setIsEditingTextoBase] = useState(false);
  const [textoEditando, setTextoEditando] = useState('');
  const [transcricaoAnterior, setTranscricaoAnterior] = useState('');
  const [isLoadingTranscricao, setIsLoadingTranscricao] = useState(false);
  const [aprimorarHabilitado, setAprimorarHabilitado] = useState(false);
  const pausaManualRef = useRef(false);

  const canvasRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const recognitionRef = useRef(null);
  const streamRef = useRef(null);
  const animationIdRef = useRef(null);
  const isRecordingRef = useRef(false);
  const textFieldRef = useRef(null);
  const transcriptionFieldRef = useRef(null);
  const isEditingTextoBaseRef = useRef(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const salvarConfiguracaoMicrofone = (microfoneId) => {
    localStorage.setItem('configuracaoMicrofone', microfoneId);
  };

  const carregarConfiguracaoMicrofone = () => {
    return localStorage.getItem('configuracaoMicrofone') || '';
  };

  const pararMicrofone = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
    }
    if (audioCtxRef.current) {
      audioCtxRef.current.close();
    }
    if (animationIdRef.current) {
      cancelAnimationFrame(animationIdRef.current);
    }
  };

  const pararGravacaoAudio = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
    }
  };

  const pararGravacao = () => {
    pararMicrofone();
    pararGravacaoAudio();
  };

  const iniciarMicrofone = () => {
    if (recognitionRef.current) {
      recognitionRef.current.start();
    }
  };

  const iniciarGravacaoAudio = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.start();
    }
  };

  const iniciarGravacao = () => {
    iniciarMicrofone();
    iniciarGravacaoAudio();
  };

  const configurarMediaRecorder = async () => {
    const audio = {
      channelCount: 1,           
      sampleRate: 44100,
      sampleSize: 16,
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true
    };
    const restricoes = {
      audio: selectedMicrophone ? {
        ...audio, 
        deviceId: { exact: selectedMicrophone } 
      } : audio
    };
  
    streamRef.current = await navigator.mediaDevices.getUserMedia(restricoes);

    mediaRecorderRef.current = new MediaRecorder(streamRef.current);
    mediaRecorderRef.current.ondataavailable = handleDataAvailable;
    mediaRecorderRef.current.onstop = handleRecordingStop;
    mediaRecorderRef.current.onerror = () => {
      console.log("Erro no gravador detectado");
    };
  };

  useEffect(() => {
    let intervalId;
    
    if (isRecording && recognitionRef.current) {
      intervalId = setInterval(async () => {
        pararGravacaoAudio();
        setTimeout(async () => {
          try {
            iniciarGravacaoAudio();
          } catch (err) {
            console.error('Erro ao recriar gravação:', err);
          }
        }, 1000);
      },120000); 
    }
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isRecording]);

  const loadAvailableMicrophones = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const microphones = devices.filter(device => device.kind === 'audioinput');
      setAvailableMicrophones(microphones);
      
      const microfoneConfigurado = carregarConfiguracaoMicrofone();
      if (microfoneConfigurado) {
        setSelectedMicrophone(microfoneConfigurado);
      }
      
    } catch (err) {
      console.error(err);
    }
  };

  const configurarVisualizador = (stream) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const canvasCtx = canvas.getContext('2d');
    
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;
    
    audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    const src = audioCtxRef.current.createMediaStreamSource(stream);
    analyserRef.current = audioCtxRef.current.createAnalyser();
    src.connect(analyserRef.current);
    analyserRef.current.fftSize = 256;
    const comprimentoBuffer = analyserRef.current.frequencyBinCount;
    const arrayDados = new Uint8Array(comprimentoBuffer);

    const desenhar = () => {
      animationIdRef.current = requestAnimationFrame(desenhar);
      analyserRef.current.getByteTimeDomainData(arrayDados);
      canvasCtx.fillStyle = '#fff';
      canvasCtx.fillRect(0, 0, canvas.width, canvas.height);
      canvasCtx.lineWidth = 2;
      canvasCtx.strokeStyle = '#0f0';
      canvasCtx.beginPath();
      
      const larguraFatia = canvas.width / comprimentoBuffer;
      let x = 0;
      for (let i = 0; i < comprimentoBuffer; i++) {
        const v = arrayDados[i] / 128.0;
        const y = (v * canvas.height) / 2;
        if (i === 0) canvasCtx.moveTo(x, y);
        else canvasCtx.lineTo(x, y);
        x += larguraFatia;
      }
      canvasCtx.lineTo(canvas.width, canvas.height / 2);
      canvasCtx.stroke();
    };
    desenhar();
  };



  const toggleEditTextoBase = () => {
    if (isEditingTextoBase && textoEditando.trim()) {
      setTextoBase(textoBase + textoEditando);
      setTextoEditando('');
    }
    const novoValor = !isEditingTextoBase;
    setIsEditingTextoBase(novoValor);
    isEditingTextoBaseRef.current = novoValor;
  };

  const desabilitarAprimorar = () => {
    setAprimorarHabilitado(false);
  };

  const habilitarAprimorar = () => {
    const prontuarioData = JSON.parse(localStorage.getItem('prontuarioIa') || '[]');
    const itemExistente = prontuarioData.findIndex(item => item.id === idAgendamento);
    if (itemExistente !== -1) {
      prontuarioData[itemExistente].habilitaAprimorar = true;
    } else {
      prontuarioData.push({ id: idAgendamento, habilitaAprimorar: true });
    }
    localStorage.setItem('prontuarioIa', JSON.stringify(prontuarioData));
    setAprimorarHabilitado(true);
  };

  const handleDataAvailable = (e) => {
    if (e.data.size > 0) {
      chunksRef.current.push(e.data);
    }
  };

  const handleRecordingStop = async (e) => {
    pausaManualRef.current = !isRecordingRef.current;
    
    const recordedBlob = new Blob(chunksRef.current, { 
      type: mediaRecorderRef.current.mimeType 
    });
    const base64Audio = await blobToBase64(recordedBlob);
    
    await enviarAudio(recordedBlob, base64Audio);
    
    chunksRef.current = [];
  };

  const processarResultadoFala = (evt) => {
    for (let i = evt.resultIndex; i < evt.results.length; i++) {
      const res = evt.results[i];
      if (res.isFinal) {
        if (!isEditingTextoBaseRef.current) {
          const texto = res[0].transcript.trim() + '\n';
          setTextoBase((textoBase)=> textoBase + texto);
        } else if (isEditingTextoBaseRef.current) {
          const texto = res[0].transcript.trim() + '\n';
          setTextoEditando((textoEditando)=> textoEditando + texto);
        }
      } 
    }
    rolarParaFinal();
  };

  const reiniciarReconhecimento = () => {
    if (!pausaManualRef.current) {
      
      try {
        recognitionRef.current.start();
        console.log('current', recognitionRef.current )
       
      } catch (err) {
        console.error('Erro ao reiniciar reconhecimento:', err);
      }
    }
    
    verificarAprimorarHabilitado()
  };

  const configurarReconhecimentoFala = async() => {

   

    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    recognitionRef.current = new SR();
    recognitionRef.current.lang = 'pt-BR';
    recognitionRef.current.continuous = true;
    recognitionRef.current.interimResults = false;
    recognitionRef.current.maxAlternatives = 1;
    recognitionRef.current.onresult = processarResultadoFala;
    recognitionRef.current.onstart = desabilitarAprimorar;
    recognitionRef.current.onend = reiniciarReconhecimento
    recognitionRef.current.onerror =  (e) => {
      console.log("Erro na transcrição detectada", e);
    };
        
    try {
        await configurarMediaRecorder();
        
      } catch (err) {
        console.error('Erro ao configurar áudio:', err);
      }
  };

  const trocarMicrofone = async () => {
    if (isRecording) {
     
      pararGravacao();
      try {
       
        await configurarMediaRecorder();
        await configurarReconhecimentoFala();
        iniciarGravacao()
       
      } catch (err) {
        console.error('Erro ao trocar microfone:', err);
        setIsRecording(false);
        isRecordingRef.current = false;
        if (onRecordingChange) {
          onRecordingChange(false);
        }
      }
    }
  };


  const enviarAudio = async (recordedBlob, base64Audio) => {
    const profissional = JSON.parse(localStorage[`_immortal|profissional_saude_logado`] || null);
    try {
      const mimeType = mediaRecorderRef.current?.mimeType || recordedBlob.type;
      const extensao = mimeType.split('/')[1].split(';')[0];
      
      const objetoAmazonS3DTO = {
        nome: `audio_${Date.now()}`,
        extensao,
        base64: base64Audio
      };
      
      await uploadArquivoAudio(
        objetoAmazonS3DTO,
        { id: profissional?.id },
        { id: sujeitoAtencaoId },
        idAgendamento && { id: idAgendamento }
      );
      habilitarAprimorar();
    } catch (error) {
      console.error('Erro ao enviar áudio:', error);
    }
  };

  const alternarGravacao = async () => {
    if(Boolean(prontuarioEstruturado)){
        setProntuarioEstruturado(null);
      }
    if (transcricaoAnterior) {
      handleVoltarTranscricao()
    }
    if (!isRecording) {
      try {
        await configurarReconhecimentoFala();
        iniciarGravacao()
      } catch (err) {
        console.error('Erro ao acessar microfone:', err);
        return;
      }
    } else {
      pararGravacao();
    }
     
    setIsRecording(!isRecording);
    isRecordingRef.current = !isRecording;
    if (onRecordingChange) {
      onRecordingChange(!isRecording);
    }
  };

  useEffect(() => {
    loadAvailableMicrophones();
    navigator.mediaDevices.addEventListener('devicechange', loadAvailableMicrophones);
    
    const prontuarioData = JSON.parse(localStorage.getItem('prontuarioIa') || '[]');
    const itemSalvo = prontuarioData.find(item => item.id === idAgendamento);
    if (itemSalvo?.textoManual) {
      setTextoBase(itemSalvo.textoManual);
    }
    if (itemSalvo?.transcricaoAnterior) {
      setTranscricaoAnterior(itemSalvo.transcricaoAnterior);
    }
     verificarAprimorarHabilitado();
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', loadAvailableMicrophones);
      pararGravacao();
      setIsRecording(false);
      isRecordingRef.current = false;
      if (onRecordingChange) {
        onRecordingChange(false);
      }
    };
    
  }, [idAgendamento]);

  useEffect(() => {
    if (recordingState !== undefined && recordingState !== isRecording) {
      setIsRecording(recordingState);
      isRecordingRef.current = recordingState;
    }
  }, [recordingState]);

  useEffect(() => {
    if (isRecording && canvasRef.current && streamRef.current) {
      configurarVisualizador(streamRef.current);
    }
    
  }, [isRecording,streamRef.current]);



  useEffect(() => {
    const timer = setTimeout(() => {
      const prontuarioData = JSON.parse(localStorage.getItem('prontuarioIa') || '[]');
      
      if (!textoBase.trim()) {
        const novoArray = prontuarioData.filter(item => item.id !== idAgendamento);
        localStorage.setItem('prontuarioIa', JSON.stringify(novoArray));
      } else {
        const itemAtual = prontuarioData.find(item => item.id === idAgendamento) || {};
        const novoItem = {
          ...itemAtual,
          id: idAgendamento,
          textoManual: textoBase.trim(),
          transcricaoAnterior: transcricaoAnterior
        };
        
        const itemExistente = prontuarioData.findIndex(item => item.id === idAgendamento);
        if (itemExistente !== -1) {
          prontuarioData[itemExistente] = novoItem;
        } else {
          prontuarioData.push(novoItem);
        }
        
        localStorage.setItem('prontuarioIa', JSON.stringify(prontuarioData));
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [textoBase, idAgendamento, transcricaoAnterior]);

  const rolarParaFinal = () => {
    if (textFieldRef.current) {
      const element = textFieldRef.current.querySelector ? 
        textFieldRef.current.querySelector('textarea, input') || textFieldRef.current :
        textFieldRef.current;
        element.scrollTop = element.scrollHeight;
    }
    if (transcriptionFieldRef.current) {
      transcriptionFieldRef.current.scrollTop = transcriptionFieldRef.current.scrollHeight;
    }
  };

  const handleEstruturarProntuario = async (tipo) => {
    
    if (textoBase.trim()) {
      setIsLoading(true);
      
      if (isRecording) {
        pararGravacao();
         setIsRecording(false);
        isRecordingRef.current = false;
        if (onRecordingChange) {
          onRecordingChange(false);
        }
      }
      
      try {
        const textoLimpo = textoBase.replace(/\n/g, ' ').trim();     
        const resposta = tipo === "CONSULTA" ? await aiEstruturarProntuarioConsulta(textoLimpo) : await aiEstruturarProntuarioRetorno(textoLimpo);
        const dadosProntuario = JSON.parse(resposta);
        let saida = dadosProntuario?.output || '';
        saida = saida.replace(/^```html\s*/, '').replace(/\s*```$/, '');
        setProntuarioEstruturado(saida);
      } catch (error) {
        console.error('Erro ao estruturar prontuário:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleVoltar = () => {
    setProntuarioEstruturado('');
  };

  const tratarTexto = (texto) => {
    const novoTexto = texto
      .replace(/\n+\s*(?=&lt;)/g, '') 
      .replace(/\n{2,}/g, '\n') 
      .replace(/<p>/g, '<span>') 
      .replace(/<\/p>/g, '</span>') 
      .replace(/\sstyle="[^"]*"/g, '')
      .replace(/^[\s\S]*?(?=&lt;title&gt;)/g, '');
    return novoTexto;
  };
  const handleVoltarTranscricao = () => {
      setTextoBase(transcricaoAnterior);
      setTranscricaoAnterior('');
  };
  const handleMelhorarTranscricao = async () => {
    if (transcricaoAnterior) {
      handleVoltarTranscricao()
      return;
    }
    setIsLoadingTranscricao(true);
    try {
      const response = await enviarArquivosAudioParaResumo(idAgendamento);
      const novaTrascricao = JSON.parse(response?.data?.data.enviarArquivosAudioParaResumo);
      setTextoBase(novaTrascricao.transcript);
      setTranscricaoAnterior(textoBase);
    } catch (error) {
      console.error('Erro ao melhorar transcrição:', error);
      openNotification && openNotification('Erro ao melhorar transcrição', 'error');
    } finally {
      setIsLoadingTranscricao(false);
    }
  };

  const handleIncluirProntuario = () => {
    if (prontuarioEstruturado.trim()) {
      setIsIncluding(true);
      const textoTratado = tratarTexto(prontuarioEstruturado);
      prontuarioStore.mensagemProntuario = textoTratado;
      prontuarioStore
        .createEntradaProntuarioTipoTexto(
          { filter: {}, withLoading: false },
          {},
          idAgendamento
        )
        .then(() => {
          setProntuarioEstruturado('');
          const prontuarioData = JSON.parse(localStorage.getItem('prontuarioIa') || '[]');
          const novoArray = prontuarioData.filter(item => item.id !== idAgendamento);
          localStorage.setItem('prontuarioIa', JSON.stringify(novoArray));
          setTextoBase('');
          
          onClose && onClose();
        })
        .catch((error) => console.error(error))
        .finally(() => setIsIncluding(false));
    }
  };

  const verificarAprimorarHabilitado = () => {
    const dados = JSON.parse(localStorage.getItem('prontuarioIa') || '[]');
    const item = dados.find(item => item.id === idAgendamento);
    const habilitado = item?.habilitaAprimorar || false;
    setAprimorarHabilitado(habilitado);
    return habilitado;
  };

  const renderButtonText = () => {
    if (isLoadingTranscricao) {
      return 'Carregando...';
    }
    if (transcricaoAnterior) {
      return 'Voltar';
    }
    return 'Aprimorar'
     
  };
 
  const configJodit = React.useMemo(() => ({
    readonly: false,
    language: 'pt_br',
    toolbarAdaptive: false,
    buttons: ['bold', 'italic', 'underline', 'ul', 'ol', 'align'],
    iframe: true,
    iframeStyle: 'body { margin: 10px; font-family: Arial, sans-serif; }',
    allowResizeX: false,
    allowResizeY: false,
    showCharsCounter: false,
    showWordsCounter: false,
    showXPathInStatusbar: false,
    className: classes.containerTranscricao
  }), []);

  return (
    <div className={classes.container}>
      <div className={classes.gridContainer}>
        <div className={classes.microphonesList}>
          <div className={classes.selectWrapper}>
            <Mic className={classes.micSelectIcon} />
            <select
              id="prt-ati-sel-mic"
              className={classes.selectControl}
              value={selectedMicrophone}
              disabled={isLoading}
              onChange={(e) => {
                const novoIdMic = e.target.value;
                setSelectedMicrophone(novoIdMic);
                salvarConfiguracaoMicrofone(novoIdMic);
                trocarMicrofone();
              }}
            >
              <option value="">Padrão do sistema</option>
              {availableMicrophones.map((mic, index) => (
                <option key={index} value={mic.deviceId}>
                  {mic.label || `Microfone ${index + 1}`}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div></div>
        <div className={classes.recordBtnContainer}>
          <div className={classes.recordBtnWrapper}>
            <div 
              id="prt-ati-div-grv"
              className={`${classes.recordBtn} ${isRecording ? classes.recording : ''}`}
              onClick={alternarGravacao}
              style={{ 
                pointerEvents: isLoading ? 'none' : 'auto', 
                opacity: isLoading ? 0.5 : 1 
              }}
            >
              <MicrofoneMagicoIcon className={classes.micIcon} />
            </div>
          </div>
        </div>
          {isRecording ? (
            <canvas ref={canvasRef} className={classes.visualizer}></canvas>
          ) : (
            <div className={classes.transcriptionLabel}>
              <Description className={classes.transcriptionIcon} />
              <span className={classes.transcriptionIcon}>Transcrição de Áudio</span>
            </div>
          )}
      </div>
      <div className={classes.labelContainer}>
        <h2>Transcrição e anotações</h2>
        <Tooltip title="Ao clicar em Editar, a transcrição deixará de ser exibida em tempo real, permitindo realizar alterações e inserir anotações. No entanto, o que for falado durante a edição continuará sendo capturado e será inserido automaticamente após o término da edição." placement="top">
          <Button id="prt-ati-btn-edt" className={classes.editButton} onClick={toggleEditTextoBase}>
            {isEditingTextoBase ? 'Parar Edição' : 'Editar'}
          </Button>
        </Tooltip>
        { !prontuarioEstruturado && <Tooltip title={transcricaoAnterior ? "Voltar para a transcrição anterior" : "Melhore a transcrição do áudio"} placement="top">
          <Button 
            id="prt-ati-btn-apr"
            className={classNames({
              [classes.voltarTranscricaoButton]: transcricaoAnterior,
              [classes.melhorarTranscricaoButton]: !transcricaoAnterior
            })} 
            onClick={handleMelhorarTranscricao}
            disabled={isLoadingTranscricao || isRecording || !aprimorarHabilitado}
          >
            {renderButtonText()}
          </Button>
        </Tooltip>}
      </div>
      <div className={classes.editorContainer}>
        {isLoading ? (
          <div className={classes.loadingContainer}>
            <CircularProgress />
            <span>Estruturando prontuário...</span>
          </div>
        ) : prontuarioEstruturado ? (
          <JoditEditor
            value={prontuarioEstruturado}
            onChange={(content) => setProntuarioEstruturado(content)}
            config={configJodit}
            id="prt-ati-inp-edt"
          />
        ) : (
          <div className={classes.textoBaseWrapper}>
            <textarea
            id="prt-ati-inp-txt"
            ref={textFieldRef}
            value={textoBase}
            onChange={(e) => setTextoBase(e.target.value)}
            className={classes.textoBaseTextArea}
          />
          </div>
        )}
      </div>
      <div className={classes.buttonGrid}>
        <Button
          id="prt-ati-btn-con"
          className={prontuarioEstruturado ? classes.backButton : classes.structuredButton}
          onClick={prontuarioEstruturado ? handleVoltar : () => handleEstruturarProntuario("CONSULTA")}
          disabled={isLoading || isRecording}
        >
          {isLoading ? (
            <span>Carregando...</span>
          ) : prontuarioEstruturado ? (
            <span>Voltar</span>
          ) : (
            <>
              <Description className={classes.buttonIcon} />
              Estruturando Consulta
            </>
          )}
        </Button>
        <Button
          id="prt-ati-btn-ret"
          className={prontuarioEstruturado ? classes.backButton : classes.structuredButton}
          onClick={prontuarioEstruturado ? handleVoltar : () => handleEstruturarProntuario("RETORNO")}
          disabled={isLoading || isRecording}
        >
          {isLoading ? (
            <span>Carregando...</span>
          ) : prontuarioEstruturado ? (
            <span>Voltar</span>
          ) : (
            <>
              <Description className={classes.buttonIcon} />
              Estruturando Retorno
            </>
          )}
        </Button>
        <Button
          id="prt-ati-btn-inc"
          className={classes.includeButton}
          disabled={isLoading || isIncluding || isRecording}
          onClick={handleIncluirProntuario}
        >
          {isIncluding ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <Send className={classes.buttonIcon} />
          )}
          {isIncluding ? 'carregando...' : 'Incluir no prontuário'}
        </Button>
      </div>
    </div>
  );
});

const styles = theme => ({
  container: {
    fontFamily: 'Arial, sans-serif',
    height: '86%',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    '& h1': {
      marginBottom: '15px',
      fontSize: '24px',
      fontWeight: 600,
    }
  },
  labelContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom:'10px',
    gap:'5px'
  },
  editButton: {
    fontSize: '12px !important',
    padding: '4px 8px !important',
    minWidth: 'auto !important'
  },
  melhorarTranscricaoButton: {
    fontSize: '12px !important',
    padding: '4px 8px !important',
    minWidth: 'auto !important',
    backgroundColor: `${theme.palette.commons.yellow} !important`,
    color: `#ffff !important`
  },
  voltarTranscricaoButton: {
    fontSize: '12px !important',
    padding: '4px 8px !important',
    minWidth: 'auto !important',
    backgroundColor: `#657496 !important`,
    color: '#fff !important'
  },

  gridContainer: {
    position: 'relative',
    display: 'grid',
    gridTemplateColumns: 'minmax(150px, 2fr) minmax(50px, 0.5fr) minmax(150px, 2fr)',
    gap: '20px',
    marginBottom: '1rem',
    border: '1px solid #ccc',
    padding: '10px',
    borderRadius: '15px'
  },
  microphonesList: {
    display: 'flex',
    alignItems: 'center'
  },
  selectWrapper: {
    position: 'relative',
    width: '100%'
  },
  micSelectIcon: {
    position: 'absolute',
    left: '8px',
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: '20px',
    color: '#666',
    pointerEvents: 'none',
    zIndex: 1
  },
  selectControl: {
    width: '100%',
    fontSize: '12px',
    padding: '8px 8px 8px 35px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    backgroundColor: 'white',
    color: '#666',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  recordBtnContainer: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 10
  },
  recordBtnWrapper: {
    padding: '3.4px',
    borderTop: '1px solid #ccc',
    borderBottom: '1px solid #ccc',
    borderLeft: '1px solid white',
    borderRight: '1px solid white',
    borderRadius: '50%',
    zIndex: 9999,
    background: 'white'
  },
  transcriptionLabel: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    color: '#666',
    gap: '8px',
    border: '1px solid #ccc',
    padding: '8px 12px',
    borderRadius: '4px',
  },
  transcriptionIcon: {
    fontSize: '12px !important',
    color: '#666',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  recordBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#9626AC',
    padding: '2px',
    borderRadius: '50%',
    cursor: 'pointer',
    border: '3px solid #9626AC',
    fontSize: '1rem',
    color: 'white',
    height: '60px',
    width: '60px',
    backgroundClip: 'content-box',
    '&:hover': {
      backgroundColor: '#7A1E8A'
    }
  },
  recording: {
    animation: 'pulse 1s infinite'
  },
  '@keyframes pulse': {
    '0%': { transform: 'scale(1)', opacity: 1 },
    '50%': { transform: 'scale(1.1)', opacity: 0.7 },
    '100%': { transform: 'scale(1)', opacity: 1 }
  },
  micIcon: {
    fontSize: '70px',
    color: 'white',
    width: '30px',
    height: '30px',
  },
  transcription: {
    marginTop: '1rem',
    padding: '1rem',
    height: '150px',
    border: '1px solid #ccc',
    background: '#fff',
    borderRadius: '4px'
  },
  editorContainer: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto'
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    gap: '10px',
    '& span': {
      fontSize: '14px',
      color: '#666'
    }
  },
  textArea: {
    width: '100%',
    height:'100%',
    border: 'none',
    padding: '10px',
    fontSize: '14px',
    fontFamily: 'Arial, sans-serif',
    resize: 'none',
    outline: 'none',
    borderRadius: '4px',
    boxSizing: 'border-box',
  },
  visualizer: {
    width: '100%',
    height: '34px',
    background: '#fff',
    border: '1px solid #ccc',
    borderRadius: '4px',
    display: 'block'
  },
  buttonGrid: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '10px',
    marginTop: '1rem',
    '& span': {
      fontSize: '10px !important'
    }
  },
  structuredButton: {
    backgroundColor: '#9626AC !important',
    color: 'white !important',
    maxWidth: '350px',
    width:'100%',
    display: 'flex !important',
    alignItems: 'center !important',
    justifyContent: 'center !important',
    gap: '2px !important',
    fontSize: '10px !important'
  },
  backButton: {
    backgroundColor: 'transparent !important',
    color: '#666 !important',
    border: 'none !important',
    maxWidth: '350px',
    width:'100%',
    display: 'flex !important',
    alignItems: 'center !important',
    justifyContent: 'center !important',
    gap: '2px !important',
    fontSize: '10px !important'
  },
  includeButton: {
    color: 'white !important',
    maxWidth: '350px',
    width:'100%',
    display: 'flex !important',
    alignItems: 'center !important',
    justifyContent: 'center !important',
    gap: '2px !important',
    fontSize: '10px !important'
  },
  buttonIcon: {
    fontSize: '16px'
  },
  containerTranscricao:{
     display: 'flex',
    flex: 1,
    flexDirection:'column'
  },
  textoBaseWrapper: {
    flex: 1,
    border: '1px solid #ccc',
    borderRadius: '4px',
    padding: '12px',
    backgroundColor: 'white'
  },
  textoBaseTextArea: {
    width: '100%',
    height: '100%',
    border: 'none',
    padding: '3px',
    scrollPaddingTop: '12px',
    fontSize: '14px',
    fontFamily: 'Arial, sans-serif',
    resize: 'none',
    outline: 'none',
    backgroundColor: 'transparent'
  },
  
});

export default inject('prontuarioStore')(withStyles(styles)(AudioTextoIA));